/**
 * App root – defines client-side routing.
 */

import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; message: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-studio-950">
          <div className="text-center px-8">
            <h2 className="text-stone-100 text-2xl font-bold mb-3">Something went wrong</h2>
            <p className="text-stone-500 mb-6">{this.state.message}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-amber-500 text-studio-950 font-semibold rounded-lg hover:bg-amber-400"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import { AdminDashboard } from './components/Upload/AdminDashboard';
import { GameEngine } from './components/Game/GameEngine';
import { ResultsScreen } from './components/Results/ResultsScreen';
import { NoteDurationGame } from './components/Education';
import { LearnAndPractice } from './pages/LearnAndPractice';
import { HomePage } from './pages/HomePage';
import { UnifiedLanding } from './pages/UnifiedLanding';
import { AfricanDrums } from './pages/AfricanDrums';
import { DrumChallenges } from './features/drums/pages/DrumChallenges';
import { DrumLeaderboard } from './features/drums/pages/DrumLeaderboard';
import { DrumLanding } from './features/drums/pages/DrumLanding';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { Login, Register } from './pages/Auth';
import { PrivateRoute } from './components/Auth';
import { AuthProvider, useAuth } from './context/AuthContext';

/**
 * Which landing owns "/". Swap this one line by hand:
 *
 *   LANDINGS.UnifiedLanding — both halves: uploaded tracks and African drums
 *   LANDINGS.HomePage       — the original, track-only landing
 *
 * Both stay imported and typechecked either way, so flipping this is the
 * only edit needed.
 */
const LANDINGS = { UnifiedLanding, HomePage };
const LANDING = LANDINGS.UnifiedLanding;

const GamePage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  return <GameEngine trackId={id} />;
};

const NavBar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-8 lg:px-16 py-5 bg-studio-950/80 backdrop-blur-md border-b border-studio-800/60 transition-all duration-300">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-baseline gap-1 group">
          <span className="font-display text-2xl font-bold text-stone-100 tracking-tight group-hover:text-amber-500 transition-colors">
            Rhythm
          </span>
          <span className="font-display text-2xl font-bold text-amber-500 italic">
            Sense
          </span>
        </Link>

        {/* Nav Links */}
        <div className="flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-semibold transition-colors ${
              isActive('/') ? 'text-amber-500' : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            Library
          </Link>
          
          <Link
            to="/leaderboard"
            className={`text-sm font-semibold transition-colors ${
              isActive('/leaderboard') ? 'text-amber-500' : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            Leaderboard
          </Link>
          
          <Link
            to="/learn"
            className={`text-sm font-semibold transition-colors ${
              isActive('/learn') ? 'text-amber-500' : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            Learn & Practice
          </Link>

          <Link
            to="/african-drums"
            className={`text-sm font-semibold transition-colors ${
              isActive('/african-drums') ? 'text-amber-500' : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            African Drums
          </Link>

          {!!user?.is_admin && (
            <Link
              to="/admin"
              className={`text-sm font-semibold transition-colors ${
                isActive('/admin') ? 'text-amber-500' : 'text-stone-500 hover:text-stone-300'
              }`}
            >
              Studio
            </Link>
          )}
        </div>

        {/* Auth Section */}
        <div className="flex items-center gap-4">
          {user ? (
            <div className="flex items-center gap-6">
              <span className="text-stone-500 text-sm font-mono hidden sm:block">{user.username}</span>
              <button
                type="button"
                onClick={handleLogout}
                className="text-stone-400 hover:text-coral-400 text-sm font-semibold transition-colors"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="text-stone-400 hover:text-stone-200 text-sm font-semibold transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-5 py-2.5 bg-amber-500 text-studio-950 text-sm font-bold rounded-xl hover:bg-amber-400 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Get Started
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-studio-950">
    <NavBar />
    <main className="flex-1 pt-20">{children}</main>
  </div>
);

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    
    <Route
      path="/"
      element={<Shell>{<LANDING />}</Shell>}
    />
    <Route
      path="/admin"
      element={
        <Shell>
          <PrivateRoute adminOnly>
            <AdminDashboard />
          </PrivateRoute>
        </Shell>
      }
    />
    <Route 
      path="/game/:id" 
      element={
        <Shell>
          <PrivateRoute>
            <GamePage />
          </PrivateRoute>
        </Shell>
      } 
    />
    <Route
      path="/results"
      element={
        <Shell>
          <ResultsScreen />
        </Shell>
      }
    />
    <Route
      path="/leaderboard"
      element={
        <Shell>
          <LeaderboardPage />
        </Shell>
      }
    />
    {/*
      The drum front door. Swap the element below by hand to choose which view
      owns this route: <DrumLanding /> for the landing page, <AfricanDrums />
      for the original soundboard. Both are self-contained.
    */}
    <Route
      path="/african-drums"
      element={
        <Shell>
          <DrumLanding />
        </Shell>
      }
    />
    <Route
      path="/drum-soundboard"
      element={
        <Shell>
          <AfricanDrums />
        </Shell>
      }
    />
    <Route
      path="/drum-challenges"
      element={
        <Shell>
          <DrumChallenges />
        </Shell>
      }
    />
    <Route
      path="/drum-leaderboard"
      element={
        <Shell>
          <DrumLeaderboard />
        </Shell>
      }
    />
    <Route
      path="/education/notes"
      element={
        <Shell>
          <PrivateRoute>
            <NoteDurationGame />
          </PrivateRoute>
        </Shell>
      }
    />
    <Route
      path="/learn"
      element={
        <Shell>
          <PrivateRoute>
            <LearnAndPractice />
          </PrivateRoute>
        </Shell>
      }
    />
  </Routes>
);

const App: React.FC = () => (
  <BrowserRouter>
    <AuthProvider>
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
